"use client";

import React, { createContext, useContext, useCallback, useMemo } from "react";
import type {
  DetailContextValue,
  DetailContextProviderProps,
  ParentEntity,
  ParentEntityType,
  LinkedCompanyAccount,
} from "./DetailContextProvider.types";

/**
 * DetailContext - Provides parent entity information to nested components
 * Used to pass context (contact/company ID) to nested tables in detail pages/modals
 */
const DetailContext = createContext<DetailContextValue | null>(null);

/**
 * DetailContextProvider - Wraps detail pages and modals to provide parent entity context
 *
 * Usage:
 * ```tsx
 * <DetailContextProvider parentEntity={{ type: 'contact', id: contactId, name: contactName }}>
 *   <DetailPageContent />
 * </DetailContextProvider>
 * ```
 */
export function DetailContextProvider({
  children,
  parentEntity,
  linkedCompanyAccount = null,
}: DetailContextProviderProps) {
  // Memoize the clear context function
  const clearContext = useCallback(() => {
    // Context is cleared by unmounting the provider or passing null parentEntity
    // This is handled by the parent component
  }, []);

  // Memoize the context value
  const contextValue = useMemo<DetailContextValue>(
    () => ({
      parentEntity: parentEntity || { type: null, id: null },
      linkedCompanyAccount: linkedCompanyAccount ?? null,
      isContextActive: !!(parentEntity?.id && parentEntity?.type),
      clearContext,
    }),
    [parentEntity, linkedCompanyAccount, clearContext]
  );

  return <DetailContext.Provider value={contextValue}>{children}</DetailContext.Provider>;
}

/**
 * Hook to access detail context
 * Returns the current parent entity context
 *
 * Usage:
 * ```tsx
 * const { parentEntity, isContextActive } = useDetailContext();
 * ```
 */
export function useDetailContext(): DetailContextValue {
  const context = useContext(DetailContext);

  if (!context) {
    // Return a default context if not wrapped in provider
    // This allows components to work both with and without context
    return {
      parentEntity: { type: null, id: null },
      linkedCompanyAccount: null,
      isContextActive: false,
      clearContext: () => {},
    };
  }

  return context;
}

/**
 * Hook to check if detail context is active
 * Useful for conditional rendering
 */
export function useIsDetailContextActive(): boolean {
  const { isContextActive } = useDetailContext();
  return isContextActive;
}

/**
 * Hook to get parent entity ID if context is active
 * Returns null if context is not active
 */
export function useParentEntityId(): string | null {
  const { parentEntity, isContextActive } = useDetailContext();
  return isContextActive ? parentEntity.id : null;
}

/**
 * Hook to get parent entity type if context is active
 * Returns null if context is not active
 */
export function useParentEntityType(): ParentEntityType {
  const { parentEntity, isContextActive } = useDetailContext();
  return isContextActive ? parentEntity.type : null;
}

/**
 * Linked CRM company (`companies.id`) when exposed by detail context (e.g. deal → deals.company_id).
 */
export function useLinkedCompanyAccountFromDetail(): LinkedCompanyAccount | null {
  const { linkedCompanyAccount } = useDetailContext();
  return linkedCompanyAccount?.id ? linkedCompanyAccount : null;
}

/**
 * Convenience: active deal surface with its linked CRM company — for integrations scoped to deals.company_id.
 */
export function useDealWithLinkedCompanyFromDetail(): {
  dealId: string;
  linkedCompany: LinkedCompanyAccount;
} | null {
  const { parentEntity, linkedCompanyAccount } = useDetailContext();
  if (
    parentEntity.type !== "deal" ||
    !parentEntity.id ||
    !linkedCompanyAccount?.id
  ) {
    return null;
  }
  return { dealId: parentEntity.id, linkedCompany: linkedCompanyAccount };
}
